import { Box, Button, Typography, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";
import { BlInput } from "Shared/Inputs/BlInput";

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, displayName?: string) => Promise<unknown>;
  isLoading: boolean;
}

export const UploadDocumentModal = ({
  isOpen,
  onClose,
  onUpload,
  isLoading,
}: UploadDocumentModalProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState("");

  const handleClose = () => {
    setSelectedFile(null);
    setDisplayName("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;
    try {
      await onUpload(selectedFile, displayName || undefined);
      handleClose();
    } catch {
      toast.error(t(Resources.Documents.UploadError));
    }
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Documents.UploadModalTitle)}
      onCancel={handleClose}
      onConfirm={selectedFile ? handleConfirm : undefined}
      confirmButtonText={t(Resources.Documents.UploadButton)}
      isLoading={isLoading}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: Spacing.M }}>
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.odt,.rtf,.csv"
            style={{ display: "none" }}
            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: "none" }}
          >
            {t(Resources.Documents.ChooseFile)}
          </Button>
          {selectedFile && (
            <Typography
              variant="caption"
              sx={{ ml: Spacing.S, color: theme.palette.text.secondary }}
            >
              {selectedFile.name}
            </Typography>
          )}
        </Box>
        <BlInput
          labelProps={{ label: t(Resources.Documents.DisplayNameLabel) }}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t(Resources.Documents.DisplayNameLabel)}
        />
      </Box>
    </BlModal>
  );
};
